import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FiCheckCircle, FiChevronDown, FiSearch, FiZap } from "react-icons/fi";
import { useTitle } from "../../../hooks/useTitle";
import { useAuth } from "../../../services/useAuth";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Button from "../../../components/BaseUI/Button";
import Input from "../../../components/BaseUI/Input";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import { withAuth } from "../../../components/HOC/withAuth";
import "./ProductionLines.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

function getInvId(v) {
  return (v?._id || v || "").toString();
}

function getMachineId(v) {
  return (v?._id || v || "").toString();
}

function ProductionEntry() {
  useTitle("Rovion MES - Üretim Girişi");
  const { getToken } = useAuth();

  const h = useCallback(
    () => ({ Authorization: `Bearer ${getToken()}` }),
    [getToken],
  );

  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toasts, setToasts] = useState([]);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedOutput, setSelectedOutput] = useState(null);
  const [quantity, setQuantity] = useState("");

  const addToast = (title, descriptionOrType = "info", maybeType = "info") => {
    const knownTypes = ["success", "warning", "danger", "info"];
    const isLegacy = knownTypes.includes(descriptionOrType);
    const t = {
      id: Date.now(),
      title,
      description: isLegacy ? undefined : descriptionOrType,
      type: isLegacy ? descriptionOrType : maybeType,
    };
    setToasts((p) => [...p, t]);
    setTimeout(() => setToasts((p) => p.filter((x) => x.id !== t.id)), 4000);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [linesRes] = await Promise.all([
        axios.get(
          `${API_URL}/api/production-lines/from-parts?subOnly=true`,
          { headers: h() },
        ),
      ]);
      const rawLines = linesRes.data?.lines || linesRes.data || [];
      setLines(Array.isArray(rawLines) ? rawLines : []);
    } catch {
      addToast("Veriler yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [h]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e) => {
      if (!e.target.closest(".pe-dropdown-wrap")) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropdownOpen]);

  const outputOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];

    lines.forEach((line) => {
      (line.machineItems || []).forEach((mi, idx) => {
        const mid = getMachineId(mi.machineId);
        const outId = getInvId(mi.outputItemId);
        if (!outId) return;

        const key = outId;
        if (seen.has(key)) return;
        seen.add(key);

        const outPart = mi.outputPartId || line.outputPartId || null;
        const name = outPart?.name || line.name || outId;
        const code = outPart?.code || line.code || "";

        const machineObj =
          typeof mi.machineId === "object" ? mi.machineId : null;
        const machineName = machineObj?.name
          ? machineObj.code
            ? `${machineObj.name} (${machineObj.code})`
            : machineObj.name
          : mid;

        opts.push({
          outputItemId: outId,
          name,
          code,
          lineId: line._id,
          machineItemIndex: idx,
          machineName,
          lineName: line.name,
        });
      });
    });

    return opts;
  }, [lines]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return outputOptions;
    const q = search.toLowerCase();
    return outputOptions.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        o.code.toLowerCase().includes(q) ||
        o.lineName.toLowerCase().includes(q),
    );
  }, [outputOptions, search]);

  const handleSelectOption = (opt) => {
    setSelectedOutput(opt);
    setDropdownOpen(false);
    setSearch("");
  };

  const handleSave = async () => {
    if (!selectedOutput) {
      addToast("Uyarı", "Lütfen bir ürün seçin", "warning");
      return;
    }
    const qty = parseInt(quantity, 10);
    if (!quantity || isNaN(qty) || qty <= 0) {
      addToast("Uyarı", "Lütfen geçerli bir miktar girin", "warning");
      return;
    }

    const line = lines.find((l) => l._id === selectedOutput.lineId);
    if (!line) return;

    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/production/record`,
        {
          lineId: line.id || `part:${line._id}`,
          stepIndex: selectedOutput.machineItemIndex,
          producedNow: qty,
        },
        { headers: h() },
      );
      addToast(
        "Kaydedildi",
        `${selectedOutput.name} için ${qty} adet üretim kaydedildi`,
        "success",
      );
      setSelectedOutput(null);
      setQuantity("");
      await fetchData();
    } catch (err) {
      addToast(
        "Hata",
        err.response?.data?.message || "Kaydetme başarısız",
        "danger",
      );
    } finally {
      setSaving(false);
    }
  };

  const isSelected = (opt) =>
    selectedOutput?.lineId === opt.lineId &&
    selectedOutput?.machineItemIndex === opt.machineItemIndex;

  return (
    <AppPage
      title="Üretim Girişi"
      subtitle="Ürettiğiniz ürünü ve miktarını girin"
      icon={<FiZap />}
    >
      <CardContainer className="p-4">
        {loading ? (
          <div className="loading-container">
            <FiZap className="spin" size={48} />
            <p>Yükleniyor...</p>
          </div>
        ) : (
          <div className="pe-form-wrap">
            {/* Step 1 */}
            <div className="pe-step">
              <div className="pe-step-header">
                <span className="pe-step-badge">1</span>
                <span className="pe-step-label">Ne ürettiniz?</span>
              </div>

              <div className="pe-dropdown-wrap">
                <button
                  type="button"
                  className={`pe-dropdown-trigger${dropdownOpen ? " is-open" : ""}${selectedOutput ? " has-value" : ""}`}
                  onClick={() => setDropdownOpen((v) => !v)}
                >
                  <span className="pe-dropdown-value">
                    {selectedOutput ? (
                      <>
                        <strong>
                          {selectedOutput.name}
                          {selectedOutput.code
                            ? ` (${selectedOutput.code})`
                            : ""}
                        </strong>
                        <small>
                          {selectedOutput.lineName} ·{" "}
                          {selectedOutput.machineName}
                        </small>
                      </>
                    ) : (
                      <span className="pe-placeholder">Ürün seçin...</span>
                    )}
                  </span>
                  <FiChevronDown
                    className={`pe-chevron${dropdownOpen ? " rotated" : ""}`}
                  />
                </button>

                {dropdownOpen && (
                  <div className="pe-dropdown-menu">
                    <div className="pe-search-wrap">
                      <FiSearch size={14} />
                      <input
                        type="text"
                        className="pe-search-input"
                        placeholder="Ara..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <ul className="pe-option-list">
                      {filteredOptions.length === 0 ? (
                        <li className="pe-option-empty">Sonuç bulunamadı</li>
                      ) : (
                        filteredOptions.map((opt, i) => (
                          <li
                            key={`${opt.lineId}-${opt.machineItemIndex}-${i}`}
                            className={`pe-option-item${isSelected(opt) ? " is-selected" : ""}`}
                            onClick={() => handleSelectOption(opt)}
                          >
                            <span className="pe-option-name">
                              {opt.name}
                              {opt.code ? ` (${opt.code})` : ""}
                            </span>
                            <span className="pe-option-sub">
                              {opt.lineName} · {opt.machineName}
                            </span>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Step 2 — shown only after selection */}
            {selectedOutput && (
              <div className="pe-step pe-step--animate">
                <div className="pe-step-header">
                  <span className="pe-step-badge">2</span>
                  <span className="pe-step-label">Kaç adet ürettiniz?</span>
                </div>

                <div className="pe-qty-wrap">
                  <Input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(v) => setQuantity(v)}
                    placeholder="0"
                    suffix={<span className="pe-unit">adet</span>}
                  />
                </div>

                <div className="pe-actions">
                  <Button
                    appearance="primary-animated"
                    onClick={handleSave}
                    loading={saving}
                    disabled={saving}
                    icon={<FiCheckCircle />}
                  >
                    {saving ? "Kaydediliyor..." : "Kaydet"}
                  </Button>
                </div>
              </div>
            )}

            {outputOptions.length === 0 && !loading && (
              <div className="empty-state mt-4">
                <FiZap size={48} />
                <p>Erişebileceğiniz üretim çıktısı bulunamadı</p>
              </div>
            )}
          </div>
        )}
      </CardContainer>

      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.title}
            description={t.description}
            appearance={t.type}
            onClose={() => setToasts((p) => p.filter((x) => x.id !== t.id))}
          />
        ))}
      </ToastContainer>

      <style>{`
        .pe-form-wrap { max-width: 600px; padding: 8px 0; }
        .pe-step { margin-bottom: 28px; }
        .pe-step--animate { animation: pe-fade-in 0.2s ease; }
        @keyframes pe-fade-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .pe-step-header { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
        .pe-step-badge {
          width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
          background: var(--m-primary); color: #fff;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 700;
        }
        .pe-step-label { font-size: 15px; font-weight: 600; color: var(--m-text); }
        .pe-dropdown-wrap { position: relative; }
        .pe-dropdown-trigger {
          width: 100%; display: flex; align-items: center; justify-content: space-between;
          padding: 10px 14px; border-radius: 8px; cursor: pointer;
          border: 1.5px solid var(--m-border); background: var(--m-card);
          transition: border-color 0.15s;
          text-align: left;
        }
        .pe-dropdown-trigger:hover, .pe-dropdown-trigger.is-open { border-color: var(--m-primary); }
        .pe-dropdown-trigger.has-value { border-color: var(--m-primary); }
        .pe-dropdown-value { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
        .pe-dropdown-value strong { font-size: 14px; font-weight: 600; color: var(--m-text); }
        .pe-dropdown-value small { font-size: 11px; color: var(--m-text-secondary, #888); }
        .pe-placeholder { font-size: 14px; color: var(--m-text-secondary, #aaa); }
        .pe-chevron { transition: transform 0.2s; flex-shrink: 0; color: var(--m-text-secondary, #888); }
        .pe-chevron.rotated { transform: rotate(180deg); }
        .pe-dropdown-menu {
          position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 500;
          background: var(--m-card); border: 1px solid var(--m-border);
          border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          overflow: hidden;
        }
        .pe-search-wrap {
          display: flex; align-items: center; gap: 8px;
          padding: 8px 12px; border-bottom: 1px solid var(--m-border);
        }
        .pe-search-input {
          flex: 1; border: none; outline: none; background: transparent;
          font-size: 13px; color: var(--m-text);
        }
        .pe-option-list { list-style: none; margin: 0; padding: 4px 0; max-height: 280px; overflow-y: auto; }
        .pe-option-item {
          padding: 10px 14px; cursor: pointer; display: flex;
          flex-direction: column; gap: 2px; transition: background 0.1s;
        }
        .pe-option-item:hover { background: var(--m-hover, rgba(0,0,0,0.04)); }
        .pe-option-item.is-selected { background: var(--m-primary-subtle, rgba(0,120,140,0.08)); }
        .pe-option-name { font-size: 14px; font-weight: 600; color: var(--m-text); }
        .pe-option-sub { font-size: 11px; color: var(--m-text-secondary, #888); }
        .pe-option-empty { padding: 14px; text-align: center; color: var(--m-text-secondary, #888); font-size: 13px; }
        .pe-qty-wrap { margin-bottom: 16px; }
        .pe-unit { font-size: 13px; font-weight: 500; color: var(--m-text-secondary, #888); padding: 0 4px; }
        .pe-actions { display: flex; gap: 10px; }
      `}</style>
    </AppPage>
  );
}

export default withAuth(ProductionEntry, ["admin", "employee", "user"]);
